<template>
  <div
    :class="[
      'modal-generic-cart-footer__wrapper',
      cartHasItems && 'modal-generic-cart-footer__wrapper--visible',
    ]"
    :style="{ '--padding-sum': `${paddingSum}px` }"
  >
    <div class="modal-generic-cart-footer__cart-wrapper">
      <div
        v-if="showCouponInput && selectedProduct"
        class="modal-generic-cart-footer__coupons"
      >
        <PromoCode
          :selectedProduct="selectedProduct"
          @set-coupon-discount="setDiscount"
        />
      </div>
      <div class="modal-generic-cart-footer__cart">
        <div class="modal-generic-cart-footer__details">
          <PillBadge
            v-if="showItemsPill"
            cart
            class="modal-generic-cart-footer__amount-items"
          >
            {{ itemsLegend }}
          </PillBadge>
          <div class="modal-generic-cart-footer__amount-price">
            <div
              v-if="cartTaxAmount"
              class="modal-generic-cart-footer__amount-price-little-letter--upper"
            >
              Sales tax: {{ formattedCartTaxAmount() }}
            </div>
            <div class="modal-generic-cart-footer__amount-price-total">
              Total today: {{ formattedCartTotalAmount() }}
            </div>
            <div
              v-if="priceLittleLetter"
              class="modal-generic-cart-footer__amount-price-little-letter"
            >
              {{ priceLittleLetter }}
            </div>
          </div>
        </div>
        <ActionLoader
          v-if="isLoading || isLoadingProp"
          name="ball-clip-rotate"
        />
        <div v-else class="modal-generic-cart-footer__actions">
          <button
            v-if="showBackBtn"
            class="btn btn--secondary"
            :disabled="isLoading || disableBackBtn"
            @click="handleBackAction()"
          >
            {{ secondaryBtnText }}
          </button>
          <button
            class="btn btn--primary"
            :disabled="!cartHasItems || isLoading || disableBuyBtn"
            @click="handleBuyAction()"
          >
            {{ primaryBtnText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PillBadge from '@/components/shared/PillBadge.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import PromoCode from './upgrade-plan-modal/PromoCode.vue'

export default {
  name: 'ModalGenericCartFooter',
  emits: ['back-action', 'buy-action'],
  components: {
    PillBadge,
    ActionLoader,
    PromoCode,
  },
  props: {
    showItemsPill: {
      type: Boolean,
      required: false,
      default: true,
    },
    showBackBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    cartTotalItems: {
      type: Number,
      required: true,
    },
    cartTotalAmount: {
      type: [Number, String],
      required: true,
      default: 0,
    },
    cartTaxAmount: {
      type: [Number, String, null],
      required: false,
      default: null,
    },
    priceLittleLetter: {
      type: [String, null],
      required: false,
      default: null,
    },
    paddingSum: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    disableBackBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableBuyBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    showCouponInput: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedProduct: {
      type: Object,
      required: false,
      default: null,
    },
    primaryBtnText: {
      type: String,
      required: false,
      default: 'Buy now',
    },
    secondaryBtnText: {
      type: String,
      required: false,
      default: 'Back',
    },
    isLoadingProp: {
      type: Boolean,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      discount: 0,
    }
  },
  computed: {
    cartHasItems() {
      return this.cartTotalItems > 0
    },
    itemsLegend() {
      return `${this.cartTotalItems} Item${this.cartTotalItems > 1 ? 's' : ''}`
    },
    isLoadingComputed() {
      return this.isLoadingProp === null ? this.isLoading : this.isLoadingProp
    },
  },
  methods: {
    handleBackAction() {
      this.isLoading =
        this.isLoadingProp === null ? true : this.isLoadingComputed
      this.$emit('back-action')
    },
    handleBuyAction() {
      this.isLoading =
        this.isLoadingProp === null ? true : this.isLoadingComputed
      this.$emit('buy-action')
    },
    setDiscount(discount) {
      this.discount = discount
    },
    formattedCartTaxAmount() {
      let amount = this.cartTaxAmount || 0
      if (typeof amount === 'string' && amount.startsWith('$')) {
        amount = parseFloat(amount.slice(1))
      }
      return `$${amount.toFixed(2)}`
    },
    formattedCartTotalAmount() {
      let amount = this.cartTotalAmount
      if (typeof amount === 'string' && amount.startsWith('$')) {
        amount = parseFloat(amount.slice(1))
      }
      return `$${(amount - this.discount).toFixed(2)}`
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-generic-cart-footer {
  &__cart {
    display: flex;
    justify-content: space-between;
    width: 100%;
    &-wrapper {
      display: flex;
      flex-direction: column;
    }
  }
  &__wrapper {
    background: var(--glass-mid-gray);
    overflow: hidden;
    transition: all 1s ease-in-out;
    max-height: 0;
    padding: 0 30px;
    width: calc(100% + var(--padding-sum));
    &--visible {
      padding: 20px 30px;
      max-height: 160px;
      height: 100%;
    }
  }
  &__details {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--white);
    text-align: left;
  }
  &__amount-price {
    &-total {
      font-size: rem(20px);
      font-style: normal;
      font-weight: 700;
      line-height: rem(26px);
    }
    &-little-letter {
      color: var(--light-blue-gray);
      font-size: rem(10px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(12px);
      &--upper {
        font-size: rem(14px);
        line-height: rem(20px);
      }
    }
  }
  &__actions {
    display: flex;
    gap: 10px;
  }
}
</style>
